import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import type { CountdownMode } from '@builder4/shared/components/Countdown';

interface StyleFieldProps {
  modes?: CountdownMode;
  onClick?: (style: CountdownMode) => void;
  className?: string;
}

const styles: Array<CountdownMode> = ['standard', 'daily', 'evergreen'];

export const CountdownModes: React.FC<StyleFieldProps> = ({
  modes = 'standard',
  onClick,
  className,
}) => {
  const [selectedStyle, setSelectedStyle] = useState(modes);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false); // 👉 For animation

  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    standard: null,
    daily: null,
    evergreen: null,
  });

  useEffect(() => {
    if (modes !== selectedStyle) {
      setSelectedStyle(modes);
    }
  }, [modes]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedStyle];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedStyle]);

  const handleClick = (newStyle: CountdownMode) => {
    setIsAnimated(true); // 👉 Run animation
    setSelectedStyle(newStyle);
    if (onClick) onClick(newStyle);
  };

  return (
    <div className={clsx('flex items-center justify-center', className)}>
      <div className="relative flex h-8 items-center rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
        <div
          className={`absolute top-0 z-20 flex h-full items-center justify-center rounded-md bg-[#ffffff] text-indigo-500 outline outline-offset-[-1px] outline-[#e9ecf0] ${isAnimated ? 'transition-all duration-300' : ''}`}
          style={{
            width: `${highlightStyle.width}px`,
            transform: `translateX(${highlightStyle.left}px)`,
          }}
        >
          <p className="text-button-sm !font-semibold capitalize">{selectedStyle}</p>
        </div>

        {styles.map((sz) => (
          <button
            key={sz}
            ref={(el) => {
              optionRefs.current[sz] = el;
            }}
            onClick={() => handleClick(sz)}
            className="text-button-sm relative z-10 min-w-10 cursor-pointer px-3 py-1.5 text-neutral-400 capitalize"
            data-testid={`style-${sz}`}
          >
            {sz}
          </button>
        ))}
      </div>
    </div>
  );
};
